import { useEffect, useLayoutEffect, useRef, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import LandingPage from "@/pages/LandingPage";
import Dashboard from "@/pages/Dashboard";
import IntakeForm from "@/pages/IntakeForm";
import Consultation from "@/pages/Consultation";
import Orientation from "@/pages/Orientation";
import RelocationPlan from "@/pages/RelocationPlan";
import CountryDetail from "@/pages/CountryDetail";
import CountryPreviewPage from "@/pages/CountryPreviewPage";
import CountryExploreOrPreviewPage from "@/pages/CountryExploreOrPreviewPage";
import PaymentSuccess from "@/pages/PaymentSuccess";
import MethodologyFAQs from "@/pages/MethodologyFAQs";
import HowRelocationClarityWorks from "@/pages/HowRelocationClarityWorks";
import InsightsHub from "@/pages/InsightsHub";
import About from "@/pages/About";
import CreateCountryShortlistForMovingAbroad from "@/pages/CreateCountryShortlistForMovingAbroad";
import WhatCountryShouldIMoveTo from "@/pages/WhatCountryShouldIMoveTo";
import BestCountriesForAmericansToMoveAbroad from "@/pages/BestCountriesForAmericansToMoveAbroad";
import BestCountriesForRemoteWorkersAndFreelancers from "@/pages/BestCountriesForRemoteWorkersAndFreelancers";
import HowMuchMoneyDoYouNeedToLiveAbroad from "@/pages/HowMuchMoneyDoYouNeedToLiveAbroad";
import CountriesWithBestHealthcareForExpats from "@/pages/CountriesWithBestHealthcareForExpats";
import PortugalVsSpainForAmericanRetirees from "@/pages/PortugalVsSpainForAmericanRetirees";
import PortugalVsSpainLivingAbroad from "@/pages/PortugalVsSpainLivingAbroad";
import WhyCostToRetireAbroadChartsAreMisleading from "@/pages/WhyCostToRetireAbroadChartsAreMisleading";
import WhyReadingAboutCountriesIsntTheSameAsChoosingOne from "@/pages/WhyReadingAboutCountriesIsntTheSameAsChoosingOne";
import TheBiggestMistakePeopleMakeWhenChoosingACountry from "@/pages/TheBiggestMistakePeopleMakeWhenChoosingACountry";
import Contact from "@/pages/Contact";
import Auth from "@/pages/Auth";
import DemoLogin from "@/pages/DemoLogin";
import RefundPolicy from "@/pages/RefundPolicy";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import SamplePlan from "@/pages/SampleGuide";
import CompareCountries from "@/pages/CompareCountries";
import { hasGaMeasurementId, trackGaEvent, trackGaPageView } from "@/lib/ga4";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SITE_URL = "https://relocationclarity.com";
const LEGACY_INSIGHT_ROUTE_REDIRECTS = {
  "/why-reading-about-countries-isnt-the-same-as-choosing-one": "/insights/why-reading-about-countries-isnt-the-same-as-choosing-one",
  "/the-biggest-mistake-people-make-when-choosing-a-country": "/insights/the-biggest-mistake-people-make-when-choosing-a-country",
  "/create-country-shortlist-for-moving-abroad": "/insights/create-country-shortlist-for-moving-abroad",
  "/what-country-should-i-move-to": "/insights/what-country-should-i-move-to",
  "/best-countries-for-americans-to-move-abroad": "/insights/best-countries-for-americans-to-move-abroad",
  "/best-countries-for-remote-workers-and-retirees": "/insights/best-countries-for-remote-workers-and-freelancers",
  "/best-countries-for-remote-workers-and-freelancers": "/insights/best-countries-for-remote-workers-and-freelancers",
  "/insights/best-countries-for-remote-workers-and-retirees": "/insights/best-countries-for-remote-workers-and-freelancers",
  "/how-much-money-do-you-need-to-live-abroad": "/insights/how-much-money-do-you-need-to-live-abroad",
  "/countries-with-the-best-healthcare-for-expats": "/insights/countries-with-the-best-healthcare-for-expats",
  "/portugal-vs-spain-for-american-retirees": "/insights/portugal-vs-spain-for-american-retirees",
  "/portugal-vs-spain-living-abroad": "/insights/portugal-vs-spain-living-abroad",
  "/why-cost-to-retire-abroad-charts-are-misleading": "/insights/why-cost-to-retire-abroad-charts-are-misleading",
};
const DEFAULT_PUBLIC_METADATA = {
  title: "Relocation Clarity | Make a Clear Decision About Living Abroad",
  description: "Compare countries and make a clear decision about where to live abroad based on cost, healthcare, residency paths, and everyday lifestyle fit.",
  canonical: `${SITE_URL}/`,
};
const PUBLIC_ROUTE_METADATA = {
  "/": DEFAULT_PUBLIC_METADATA,
  "/methodology": {
    title: "Methodology & FAQs | Relocation Clarity",
    description: "See how Relocation Clarity evaluates country fit across cost, healthcare, residency, and lifestyle so you can make a calmer, more grounded move abroad.",
    canonical: `${SITE_URL}/methodology`,
  },
  "/how-relocation-clarity-works": {
    title: "How Relocation Clarity Works | A Clearer Way to Decide Abroad",
    description: "See how Relocation Clarity turns your priorities into clearer country comparisons, practical tradeoffs, and a more confident move-abroad plan.",
    canonical: `${SITE_URL}/how-relocation-clarity-works`,
  },
  "/insights": {
    title: "Relocation Insights | Relocation Clarity",
    description: "Decision-focused analysis for choosing where to live abroad. Compare countries, understand tradeoffs, and make better relocation decisions with practical context.",
    canonical: `${SITE_URL}/insights`,
  },
  "/insights/why-reading-about-countries-isnt-the-same-as-choosing-one": {
    title: "Why Reading About Countries Isn't the Same as Choosing One | Relocation Clarity",
    description: "Research can tell you what a country is like. Learn why choosing where to live abroad requires clarity about your priorities before country comparison begins.",
    canonical: `${SITE_URL}/insights/why-reading-about-countries-isnt-the-same-as-choosing-one`,
  },
  "/insights/the-biggest-mistake-people-make-when-choosing-a-country": {
    title: "The Biggest Mistake People Make When Choosing a Country | Relocation Clarity",
    description: "The biggest relocation mistake is comparing countries before you know which priorities should do the filtering. Learn a calmer way to decide.",
    canonical: `${SITE_URL}/insights/the-biggest-mistake-people-make-when-choosing-a-country`,
  },
  "/about": {
    title: "About Relocation Clarity | Independent Relocation Guidance",
    description: "Learn why Relocation Clarity exists, how its independent methodology works, and the philosophy behind its decision-focused relocation guidance.",
    canonical: `${SITE_URL}/about`,
  },
  "/insights/create-country-shortlist-for-moving-abroad": {
    title: "How to Create a Country Shortlist for Moving Abroad | Relocation Clarity",
    description: "Learn how to narrow your country options and build a realistic shortlist before moving abroad, based on your budget, priorities, and daily life needs.",
    canonical: `${SITE_URL}/insights/create-country-shortlist-for-moving-abroad`,
  },
  "/insights/what-country-should-i-move-to": {
    title: "What Country Should I Move To? | Relocation Clarity",
    description: "Choosing a country to move to depends on your budget, healthcare needs, residency path, lifestyle, and tradeoffs. Learn how to compare countries more clearly before you commit.",
    canonical: `${SITE_URL}/insights/what-country-should-i-move-to`,
  },
  "/insights/best-countries-for-americans-to-move-abroad": {
    title: "Best Countries for Americans to Move Abroad | Relocation Clarity",
    description: "Compare the countries Americans most often consider for living abroad across residency practicality, healthcare, affordability, and day-to-day fit.",
    canonical: `${SITE_URL}/insights/best-countries-for-americans-to-move-abroad`,
  },
  "/insights/best-countries-for-remote-workers-and-freelancers": {
    title: "Best Countries for Remote Workers and Freelancers | Relocation Clarity",
    description: "See how countries compare for remote workers and freelancers across infrastructure, affordability, visa flexibility, and everyday work-life fit.",
    canonical: `${SITE_URL}/insights/best-countries-for-remote-workers-and-freelancers`,
  },
  "/insights/how-much-money-do-you-need-to-live-abroad": {
    title: "How Much Money Do You Need to Live Abroad? | Relocation Clarity",
    description: "Understand what realistic monthly budgets abroad actually depend on, and how to compare countries without relying on misleading averages.",
    canonical: `${SITE_URL}/insights/how-much-money-do-you-need-to-live-abroad`,
  },
  "/insights/countries-with-the-best-healthcare-for-expats": {
    title: "Countries With the Best Healthcare for Expats | Relocation Clarity",
    description: "Compare countries for expat healthcare through quality, access, peace of mind, and how strongly healthcare should shape your relocation shortlist.",
    canonical: `${SITE_URL}/insights/countries-with-the-best-healthcare-for-expats`,
  },
  "/insights/portugal-vs-spain-for-american-retirees": {
    title: "Portugal vs Spain for American Retirees | Relocation Clarity",
    description: "Compare Portugal and Spain for retirement abroad across cost, healthcare, residency, and daily pace so you can see which option fits your life better.",
    canonical: `${SITE_URL}/insights/portugal-vs-spain-for-american-retirees`,
  },
  "/insights/portugal-vs-spain-living-abroad": {
    title: "Portugal vs Spain: Which Country Fits Your Priorities? | Relocation Clarity",
    description: "Compare Portugal and Spain across cost, healthcare, residency practicality, and lifestyle tradeoffs so you can see which one fits your priorities more clearly.",
    canonical: `${SITE_URL}/insights/portugal-vs-spain-living-abroad`,
  },
  "/insights/why-cost-to-retire-abroad-charts-are-misleading": {
    title: "Why Cost Charts Don’t Tell the Whole Story | Relocation Clarity",
    description: "Cost-of-living charts can mislead your retirement decision abroad. Learn how to judge real affordability, tradeoffs, and fit before you commit.",
    canonical: `${SITE_URL}/insights/why-cost-to-retire-abroad-charts-are-misleading`,
  },
  "/contact": {
    title: "Contact | Relocation Clarity",
    description: "Contact Relocation Clarity with questions about the assessment, your plan, or how the decision process works for your move abroad.",
    canonical: `${SITE_URL}/contact`,
  },
  "/sample-plan": {
    title: "Sample Relocation Blueprint | Relocation Clarity",
    description: "Preview a sample Relocation Clarity Blueprint to see how country fit, tradeoffs, likely residency paths, and next steps are organized before you buy.",
    canonical: `${SITE_URL}/sample-plan`,
  },
  "/sample-guide": {
    title: "Sample Relocation Blueprint | Relocation Clarity",
    description: "Preview a sample Relocation Clarity Blueprint to see how country fit, tradeoffs, likely residency paths, and next steps are organized before you buy.",
    canonical: `${SITE_URL}/sample-plan`,
  },
  "/refund-policy": {
    title: "Refund Policy | Relocation Clarity",
    description: "Read the Relocation Clarity refund policy so you know how one-time purchases, eligibility, and support are handled before you buy a plan.",
    canonical: `${SITE_URL}/refund-policy`,
  },
  "/terms": {
    title: "Terms of Service | Relocation Clarity",
    description: "Review the Relocation Clarity terms so you understand how the assessment, paid plans, account access, and site use are handled before purchase.",
    canonical: `${SITE_URL}/terms`,
  },
  "/privacy": {
    title: "Privacy Policy | Relocation Clarity",
    description: "Read how Relocation Clarity collects, uses, and protects your information when you create an account, complete the assessment, or purchase a plan.",
    canonical: `${SITE_URL}/privacy`,
  },
};
const PRIVATE_ROUTE_METADATA = {
  "/dashboard": {
    title: "Dashboard | Relocation Clarity",
    description: "Review your saved results, plan access, and next steps inside your Relocation Clarity dashboard.",
  },
  "/assessment": {
    title: "Assessment | Relocation Clarity",
    description: "Complete or update your relocation assessment to refine the countries that fit your priorities and constraints.",
  },
  "/consultation": {
    title: "Consultation | Relocation Clarity",
    description: "Access decision support and relocation guidance inside Relocation Clarity.",
  },
  "/assessment-results": {
    title: "Assessment Results | Relocation Clarity",
    description: "Review your assessment results and country tradeoffs inside Relocation Clarity.",
  },
  "/plan": {
    title: "Relocation Blueprint | Relocation Clarity",
    description: "Access your personalized Relocation Blueprint, tradeoffs, and next steps inside Relocation Clarity.",
  },
  "/compare-countries": {
    title: "Compare Countries | Relocation Clarity",
    description: "Compare your shortlisted countries across the factors that matter most to your move.",
  },
  "/payment-success": {
    title: "Payment Success | Relocation Clarity",
    description: "Your purchase is complete. Access your next steps and plan flow inside Relocation Clarity.",
  },
  "/payment-cancel": {
    title: "Payment Cancelled | Relocation Clarity",
    description: "Your payment was not completed. You can return to your dashboard and continue when you’re ready.",
  },
  "/auth": {
    title: "Sign In | Relocation Clarity",
    description: "Sign in to access your assessment, results, and Relocation Blueprint.",
  },
  "/demo-login": {
    title: "Demo Login | Relocation Clarity",
    description: "Open the demo experience to explore the assessment, results, and plan flow.",
  },
};
const PUBLIC_COUNTRY_PREVIEW_NAMES = {
  "/country/portugal": "Portugal",
  "/country/spain": "Spain",
  "/country/mexico": "Mexico",
  "/country/costa-rica": "Costa Rica",
  "/country/thailand": "Thailand",
  "/country/malaysia": "Malaysia",
  "/country/france": "France",
  "/country/germany": "Germany",
  "/country/netherlands": "the Netherlands",
  "/country/panama": "Panama",
  "/country/greece": "Greece",
  "/country/canada": "Canada",
  "/country/new-zealand": "New Zealand",
  "/country/australia": "Australia",
  "/country/italy": "Italy",
  "/country/uruguay": "Uruguay",
  "/country/ecuador": "Ecuador",
  "/country/colombia": "Colombia",
  "/country/vietnam": "Vietnam",
  "/country/japan": "Japan",
  "/country/singapore": "Singapore",
  "/country/croatia": "Croatia",
  "/country/argentina": "Argentina",
  "/country/chile": "Chile",
};
const PUBLIC_COUNTRY_PREVIEW_PATHS = new Set([
  "/country/portugal",
  "/country/spain",
  "/country/mexico",
  "/country/costa-rica",
  "/country/thailand",
  "/country/malaysia",
  "/country/france",
  "/country/germany",
  "/country/netherlands",
  "/country/panama",
  "/country/greece",
  "/country/canada",
  "/country/new-zealand",
  "/country/australia",
  "/country/italy",
  "/country/uruguay",
  "/country/ecuador",
  "/country/colombia",
  "/country/vietnam",
  "/country/japan",
  "/country/singapore",
  "/country/croatia",
  "/country/argentina",
  "/country/chile",
]);
const NOINDEX_EXACT_PATHS = new Set([
  "/dashboard",
  "/assessment",
  "/consultation",
  "/assessment-results",
  "/plan",
  "/compare-countries",
  "/payment-success",
  "/payment-cancel",
  "/auth",
  "/demo-login",
]);

const shouldNoindexRoute = (pathname, hash = "") => {
  if (hash?.includes("session_id=")) return true;
  if (NOINDEX_EXACT_PATHS.has(pathname)) return true;
  if (pathname.startsWith("/admin")) return true;

  const isCountryPath = /^\/country\/[^/]+$/.test(pathname);
  if (isCountryPath && !PUBLIC_COUNTRY_PREVIEW_PATHS.has(pathname)) {
    return true;
  }

  return false;
};

const getCountryPreviewMetadata = (pathname) => {
  const countryName = PUBLIC_COUNTRY_PREVIEW_NAMES[pathname];
  return {
    title: `${countryName} Country Preview | Relocation Clarity`,
    description: `Explore ${countryName} for living abroad. See how cost, healthcare, residency, lifestyle, and relocation ease may fit your priorities before you decide.`,
    canonical: `${SITE_URL}${pathname}`,
  };
};

const getRouteMetadata = (pathname) => {
  if (PUBLIC_ROUTE_METADATA[pathname]) {
    return PUBLIC_ROUTE_METADATA[pathname];
  }

  if (PUBLIC_COUNTRY_PREVIEW_PATHS.has(pathname)) {
    return getCountryPreviewMetadata(pathname);
  }

  if (PRIVATE_ROUTE_METADATA[pathname]) {
    return PRIVATE_ROUTE_METADATA[pathname];
  }

  if (pathname.startsWith("/admin")) {
    return {
      title: "Admin Access | Relocation Clarity",
      description: "Restricted admin access for managing internal Relocation Clarity operations.",
    };
  }

  if (/^\/country\/[^/]+$/.test(pathname)) {
    return {
      title: "Country Detail | Relocation Clarity",
      description: "Review a user-specific country detail view inside your personalized relocation flow.",
    };
  }

  return DEFAULT_PUBLIC_METADATA;
};

const RouteIndexControls = () => {
  const location = useLocation();

  useEffect(() => {
    const shouldNoindex = shouldNoindexRoute(location.pathname, location.hash);
    const metaSelector = 'meta[name="robots"][data-route-robots="true"]';
    let robotsMeta = document.head.querySelector(metaSelector);

    if (shouldNoindex) {
      if (!robotsMeta) {
        robotsMeta = document.createElement("meta");
        robotsMeta.setAttribute("name", "robots");
        robotsMeta.setAttribute("data-route-robots", "true");
        document.head.appendChild(robotsMeta);
      }
      robotsMeta.setAttribute("content", "noindex, nofollow");
      return;
    }

    if (robotsMeta) {
      robotsMeta.remove();
    }
  }, [location.hash, location.pathname]);

  useEffect(() => {
    const metadata = getRouteMetadata(location.pathname);
    let descriptionMeta = document.head.querySelector('meta[name="description"]');
    let canonicalLink = document.head.querySelector('link[rel="canonical"][data-route-canonical="true"]');

    if (!descriptionMeta) {
      descriptionMeta = document.createElement("meta");
      descriptionMeta.setAttribute("name", "description");
      document.head.appendChild(descriptionMeta);
    }

    document.title = metadata.title;
    descriptionMeta.setAttribute("content", metadata.description);

    if (metadata.canonical) {
      if (!canonicalLink) {
        canonicalLink = document.createElement("link");
        canonicalLink.setAttribute("rel", "canonical");
        canonicalLink.setAttribute("data-route-canonical", "true");
        document.head.appendChild(canonicalLink);
      }
      canonicalLink.setAttribute("href", metadata.canonical);
      return;
    }

    if (canonicalLink) {
      canonicalLink.remove();
    }
  }, [location.pathname]);

  return null;
};

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        
        try {
          const response = await axios.post(
            `${API}/auth/session`,
            { session_id: sessionId },
            { withCredentials: true }
          );
          
          // Check for stored redirect destination from before OAuth
          const storedRedirect = sessionStorage.getItem("auth_redirect");
          const isReturning = sessionStorage.getItem("auth_returning") === "true";
          sessionStorage.removeItem("auth_redirect");
          sessionStorage.removeItem("auth_returning");
          
          // Determine where to redirect
          let redirectTo = "/dashboard";
          if (storedRedirect) {
            redirectTo = storedRedirect;
          } else if (!isReturning) {
            // New user via Google - check if they have intake
            try {
              const statusResponse = await axios.get(`${API}/auth/status`, { withCredentials: true });
              redirectTo = statusResponse.data.redirect_to || "/assessment";
            } catch {
              redirectTo = "/assessment";
            }
          }
          
          navigate(redirectTo, { replace: true, state: { user: response.data } });
        } catch (error) {
          console.error("Auth error:", error);
          navigate("/", { replace: true });
        }
      }
    };

    processAuth();
  }, [location, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-muted-foreground">Signing you in...</p>
      </div>
    </div>
  );
};

const ScrollToTopOnRouteChange = () => {
  const location = useLocation();

  useLayoutEffect(() => {
    if (location.hash) {
      const targetId = location.hash.replace(/^#/, "");
      const targetElement = document.getElementById(targetId);

      if (targetElement) {
        requestAnimationFrame(() => {
          targetElement.scrollIntoView({ block: "start" });
        });
        return;
      }
    }

    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname, location.search, location.hash]);

  return null;
};

const RouteAnalyticsTracker = () => {
  const location = useLocation();
  const lastTrackedPath = useRef("");

  useEffect(() => {
    if (!hasGaMeasurementId) {
      return undefined;
    }

    const pagePath = `${location.pathname}${location.search || ""}`;
    if (lastTrackedPath.current === pagePath) {
      return undefined;
    }

    const frameId = window.requestAnimationFrame(() => {
      const tracked = trackGaPageView({
        page_path: pagePath,
        page_title: document.title,
        page_location: window.location.href,
      });

      if (tracked) {
        lastTrackedPath.current = pagePath;
      }
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!hasGaMeasurementId) {
      return undefined;
    }

    const handleDocumentClick = (event) => {
      const anchor = event.target instanceof Element ? event.target.closest("a[href]") : null;
      if (!anchor) return;

      const href = anchor.getAttribute("href");
      if (!href || href.startsWith("http") || href.startsWith("mailto:") || href.startsWith("tel:")) {
        return;
      }

      let targetUrl;
      try {
        targetUrl = new URL(href, window.location.origin);
      } catch {
        return;
      }

      if (!["/sample-plan", "/sample-guide"].includes(targetUrl.pathname)) {
        return;
      }

      const linkText = (anchor.textContent || "").trim().replace(/\s+/g, " ").slice(0, 120);
      const parentWithTestId = anchor.closest("[data-testid]");

      trackGaEvent("sample_blueprint_clicked", {
        page_path: `${location.pathname}${location.search || ""}`,
        destination_path: "/sample-plan",
        link_text: linkText || null,
        link_testid: parentWithTestId?.getAttribute("data-testid") || null,
      });
    };

    document.addEventListener("click", handleDocumentClick, true);
    return () => document.removeEventListener("click", handleDocumentClick, true);
  }, [location.pathname, location.search]);

  return null;
};

const ProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  // If user data passed from AuthCallback, skip auth check
  useEffect(() => {
    if (location.state?.user) {
      setIsAuthenticated(true);
      setUser(location.state.user);
      return;
    }

    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setIsAuthenticated(true);
        setUser(response.data);
      } catch {
        setIsAuthenticated(false);
        // Redirect to auth page with current path as redirect destination
        const redirectParam = location.pathname !== "/" ? `?redirect=${encodeURIComponent(location.pathname)}` : "";
        navigate(`/auth${redirectParam}`, { replace: true });
      }
    };

    checkAuth();
  }, [location.state, location.pathname, navigate]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return typeof children === "function" ? children({ user }) : children;
};

function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id (synchronous check before routes)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            {({ user }) => <Dashboard user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/assessment"
        element={
          <ProtectedRoute>
            {({ user }) => <IntakeForm user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/consultation"
        element={
          <ProtectedRoute>
            {({ user }) => <Consultation user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/assessment-results"
        element={
          <ProtectedRoute>
            {({ user }) => <Orientation user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/plan"
        element={
          <ProtectedRoute>
            {({ user }) => <RelocationPlan user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/compare-countries"
        element={
          <ProtectedRoute>
            {({ user }) => <CompareCountries user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/country/portugal"
        element={<CountryExploreOrPreviewPage countryKey="portugal" />}
      />
      <Route
        path="/country/spain"
        element={<CountryExploreOrPreviewPage countryKey="spain" />}
      />
      <Route
        path="/country/mexico"
        element={<CountryExploreOrPreviewPage countryKey="mexico" />}
      />
      <Route
        path="/country/costa-rica"
        element={<CountryExploreOrPreviewPage countryKey="costa-rica" />}
      />
      <Route
        path="/country/thailand"
        element={<CountryExploreOrPreviewPage countryKey="thailand" />}
      />
      <Route
        path="/country/malaysia"
        element={<CountryExploreOrPreviewPage countryKey="malaysia" />}
      />
      <Route
        path="/country/france"
        element={<CountryExploreOrPreviewPage countryKey="france" />}
      />
      <Route
        path="/country/germany"
        element={<CountryExploreOrPreviewPage countryKey="germany" />}
      />
      <Route
        path="/country/netherlands"
        element={<CountryExploreOrPreviewPage countryKey="netherlands" />}
      />
      <Route
        path="/country/panama"
        element={<CountryExploreOrPreviewPage countryKey="panama" />}
      />
      <Route
        path="/country/greece"
        element={<CountryExploreOrPreviewPage countryKey="greece" />}
      />
      <Route
        path="/country/canada"
        element={<CountryExploreOrPreviewPage countryKey="canada" />}
      />
      <Route
        path="/country/new-zealand"
        element={<CountryExploreOrPreviewPage countryKey="new-zealand" />}
      />
      <Route
        path="/country/australia"
        element={<CountryExploreOrPreviewPage countryKey="australia" />}
      />
      <Route
        path="/country/italy"
        element={<CountryExploreOrPreviewPage countryKey="italy" />}
      />
      <Route
        path="/country/uruguay"
        element={<CountryExploreOrPreviewPage countryKey="uruguay" />}
      />
      <Route
        path="/country/ecuador"
        element={<CountryExploreOrPreviewPage countryKey="ecuador" />}
      />
      <Route
        path="/country/colombia"
        element={<CountryExploreOrPreviewPage countryKey="colombia" />}
      />
      <Route
        path="/country/vietnam"
        element={<CountryExploreOrPreviewPage countryKey="vietnam" />}
      />
      <Route
        path="/country/japan"
        element={<CountryExploreOrPreviewPage countryKey="japan" />}
      />
      <Route
        path="/country/singapore"
        element={<CountryExploreOrPreviewPage countryKey="singapore" />}
      />
      <Route
        path="/country/croatia"
        element={<CountryExploreOrPreviewPage countryKey="croatia" />}
      />
      <Route
        path="/country/argentina"
        element={<CountryExploreOrPreviewPage countryKey="argentina" />}
      />
      <Route
        path="/country/chile"
        element={<CountryExploreOrPreviewPage countryKey="chile" />}
      />
      <Route
        path="/country/:countryCode"
        element={
          <ProtectedRoute>
            {({ user }) => <CountryDetail user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/payment-success"
        element={
          <ProtectedRoute>
            {({ user }) => <PaymentSuccess user={user} />}
          </ProtectedRoute>
        }
      />
      <Route path="/payment-cancel" element={<Navigate to="/dashboard" replace />} />
      <Route path="/methodology" element={<MethodologyFAQs />} />
      <Route path="/how-relocation-clarity-works" element={<HowRelocationClarityWorks />} />
      <Route path="/about" element={<About />} />
      <Route path="/insights" element={<InsightsHub />} />
      {Object.entries(LEGACY_INSIGHT_ROUTE_REDIRECTS).map(([legacyPath, nextPath]) => (
        <Route key={legacyPath} path={legacyPath} element={<Navigate to={nextPath} replace />} />
      ))}
      <Route path="/insights/why-reading-about-countries-isnt-the-same-as-choosing-one" element={<WhyReadingAboutCountriesIsntTheSameAsChoosingOne />} />
      <Route path="/insights/the-biggest-mistake-people-make-when-choosing-a-country" element={<TheBiggestMistakePeopleMakeWhenChoosingACountry />} />
      <Route path="/insights/create-country-shortlist-for-moving-abroad" element={<CreateCountryShortlistForMovingAbroad />} />
      <Route path="/insights/what-country-should-i-move-to" element={<WhatCountryShouldIMoveTo />} />
      <Route path="/insights/best-countries-for-americans-to-move-abroad" element={<BestCountriesForAmericansToMoveAbroad />} />
      <Route path="/insights/best-countries-for-remote-workers-and-retirees" element={<Navigate to="/insights/best-countries-for-remote-workers-and-freelancers" replace />} />
      <Route path="/insights/best-countries-for-remote-workers-and-freelancers" element={<BestCountriesForRemoteWorkersAndFreelancers />} />
      <Route path="/insights/how-much-money-do-you-need-to-live-abroad" element={<HowMuchMoneyDoYouNeedToLiveAbroad />} />
      <Route path="/insights/countries-with-the-best-healthcare-for-expats" element={<CountriesWithBestHealthcareForExpats />} />
      <Route path="/insights/portugal-vs-spain-for-american-retirees" element={<PortugalVsSpainForAmericanRetirees />} />
      <Route path="/insights/portugal-vs-spain-living-abroad" element={<PortugalVsSpainLivingAbroad />} />
      <Route path="/insights/why-cost-to-retire-abroad-charts-are-misleading" element={<WhyCostToRetireAbroadChartsAreMisleading />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/auth" element={<Auth />} />
      <Route path="/demo-login" element={<DemoLogin />} />
      <Route path="/sample-plan" element={<SamplePlan />} />
      <Route path="/sample-guide" element={<Navigate to="/sample-plan" replace />} />
      <Route path="/refund-policy" element={<RefundPolicy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/admin/*" element={<Navigate to="/auth?redirect=%2Fadmin" replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    document.body.classList.add("rc-structure-preview");

    return () => {
      document.body.classList.remove("rc-structure-preview");
    };
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTopOnRouteChange />
        <RouteIndexControls />
        <RouteAnalyticsTracker />
        <AppRouter />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </div>
  );
}

export default App;
